import React, { FC, useCallback, useState } from "react";
import { Dialog, ButtonProps, Input } from "@xapp/components";

import { BasicIntegrationCard, IntegrationCardProp, IntegrationConnectionProps } from "./BasicIntegrationCard";

export interface ServiceMinderConnectionProps extends IntegrationConnectionProps {
    integration: "serviceminder";
    apiKey: string;
}

export interface ServiceMinderIntegrationCardProps extends IntegrationCardProp {
    apiKey?: string;
    /**
     * Optional body content for the dialog that displays below the API key input box.
     */
    bodyContent?: string | JSX.Element;
    /**
     * Connect ServiceMinder
     */
    onConnect?(props: ServiceMinderConnectionProps): void;
}

export const ServiceMinderIntegrationCard: FC<ServiceMinderIntegrationCardProps> = (props) => {
    const { checked, disabled, isLinked, isActive, onDisconnect, onConnect } = props;

    const [apiKey, setApiKey] = useState<string>(props.apiKey || "");
    const [showDialog, setShowDialog] = useState<boolean>(false);

    const handleServiceMinderClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
        e.preventDefault();

        if (typeof checked === "boolean") {
            if (typeof props.onClick === "function") {
                props.onClick(e);
            }
        } else {
            // pop up modal
            setShowDialog(true);
        }
    };

    const handleConnectServiceMinder = useCallback(() => {
        if (typeof onConnect === "function") {
            onConnect({ integration: "serviceminder", apiKey });
        }
        setShowDialog(false);
    }, [onConnect, apiKey]);

    const handleDisconnectServiceMinder = useCallback(() => {
        if (typeof onDisconnect === "function") {
            onDisconnect();
            setShowDialog(false);
        }
    }, [onDisconnect]);

    const actions: ButtonProps[] = [
        {
            label: "Close",
            variation: "secondary",
            onClick: () => {
                setShowDialog(false);
            },
        },
    ];

    if (typeof onConnect === "function") {
        actions.push({
            label: "Connect",
            disabled: !apiKey,
            onClick: handleConnectServiceMinder,
        });
    }

    return (
        <>
            <BasicIntegrationCard
                checked={checked}
                disabled={disabled}
                imageUrl="/img/integrations/serviceminder/serviceminder-logo.png"
                text="ServiceMinder"
                href={() => "/integrations/serviceminder"}
                onClick={handleServiceMinderClick}
                isLinked={isLinked}
                isActive={isActive}
                onDisconnect={typeof onDisconnect === "function" ? handleDisconnectServiceMinder : undefined}
            />
            <Dialog
                active={showDialog}
                actions={actions}
                title="ServiceMinder Setup"
                onClose={() => {
                    setShowDialog(false);
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", rowGap: "10px" }}>
                    <div>
                        <p>Connect your ServiceMinder account to automatically sync leads and customer data.</p>
                        <p>Enter your ServiceMinder API key below to get started.</p>
                    </div>
                    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                        {/* @ts-ignore */}
                        <Input label="ServiceMinder API Key" value={apiKey} onChange={setApiKey} />
                    </div>
                    <div>
                        <b>How do I find my API key?</b>
                        <p>
                            From within ServiceMinder, on the left-hand side click "Control Panel" then "API Keys".
                            Click "Add" on the first column label.
                        </p>
                    </div>
                    <div>
                        <b>What data will be synced?</b>
                        <p>
                            We will automatically sync lead information including customer name, phone number, and
                            service requests to your ServiceMinder account.
                        </p>
                    </div>
                </div>
            </Dialog>
        </>
    );
};
